import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

type SidebarIconDefinition = {
  viewBox: string;
  markup: string;
};

const DEFAULT_ICON: SidebarIconDefinition = {
  viewBox: '0 0 24 24',
  markup:
    '<g id="folder"><path d="M19.5,6.75H12.31L10.53,4.72a1.74,1.74,0,0,0-1.31-.59H4.5A1.76,1.76,0,0,0,2.75,5.88v12.5A1.76,1.76,0,0,0,4.5,20.13h15A1.76,1.76,0,0,0,21.25,18.38V8.5A1.76,1.76,0,0,0,19.5,6.75Zm.25,11.63a.25.25,0,0,1-.25.25H4.5a.25.25,0,0,1-.25-.25V5.88a.25.25,0,0,1,.25-.25H9.22a.26.26,0,0,1,.19.09L11.47,8a.76.76,0,0,0,.56.25H19.5a.25.25,0,0,1,.25.25Z"/></g>',
};

const SIDEBAR_ICON_REGISTRY: Record<string, SidebarIconDefinition> = {
  'pi pi-home': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="home"><path d="M20,11.75a.74.74,0,0,1-.45-.15L12,5.94,4.45,11.6a.75.75,0,0,1-.9-1.2l8-6a.75.75,0,0,1,.9,0l8,6a.75.75,0,0,1,.15,1A.74.74,0,0,1,20,11.75Z"/><path d="M18,19.75H6A.76.76,0,0,1,5.25,19V9.5a.75.75,0,0,1,1.5,0v8.75h10.5V9.5a.75.75,0,0,1,1.5,0V19A.76.76,0,0,1,18,19.75Z"/><path d="M14,19.75a.76.76,0,0,1-.75-.75V12.75h-2.5V19a.75.75,0,0,1-1.5,0V12a.76.76,0,0,1,.75-.75h4a.76.76,0,0,1,.75.75v7A.76.76,0,0,1,14,19.75Z"/></g>',
  },
  'pi pi-building': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="building"><rect x="9" y="6" width="2" height="2" rx="0.5"/><rect x="13" y="6" width="2" height="2" rx="0.5"/><rect x="9" y="9.5" width="2" height="2" rx="0.5"/><rect x="13" y="9.5" width="2" height="2" rx="0.5"/><rect x="9" y="13" width="2" height="2" rx="0.5"/><rect x="13" y="13" width="2" height="2" rx="0.5"/><path d="M18.25,19.25h-.5V4A.76.76,0,0,0,17,3.25H7A.76.76,0,0,0,6.25,4V19.25h-.5a.75.75,0,0,0,0,1.5h12.5a.75.75,0,0,0,0-1.5Zm-2,0H11V17a.5.5,0,0,0-.5-.5h-1A.5.5,0,0,0,9,17v2.25H7.75V4.75h8.5Z"/></g>',
  },
  'pi pi-map-marker': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="map-marker"><path d="M12,21.25a.69.69,0,0,1-.41-.13c-.3-.19-7.34-4.92-7.34-10.67a7.75,7.75,0,0,1,15.5,0c0,5.75-7,10.48-7.34,10.67A.69.69,0,0,1,12,21.25Zm0-17a6.23,6.23,0,0,0-6.25,6.2c0,4.21,4.79,8.06,6.25,9.13,1.46-1.07,6.25-4.92,6.25-9.13A6.23,6.23,0,0,0,12,4.25Z"/><path d="M12,12.75A2.75,2.75,0,1,1,14.75,10,2.75,2.75,0,0,1,12,12.75Zm0-4A1.25,1.25,0,1,0,13.25,10,1.25,1.25,0,0,0,12,8.75Z"/></g>',
  },
  'pi pi-shield': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="shield"><path d="M12,20.75a.87.87,0,0,1-.28-.05A14.27,14.27,0,0,1,3.29,6.43a.74.74,0,0,1,.61-.69,27.12,27.12,0,0,0,7.79-2.42.75.75,0,0,1,.62,0A27.12,27.12,0,0,0,20.1,5.74a.74.74,0,0,1,.61.69A14.27,14.27,0,0,1,12.28,20.7.87.87,0,0,1,12,20.75ZM4.76,7.11A12.47,12.47,0,0,0,12,19.18,12.47,12.47,0,0,0,19.24,7.11,27.56,27.56,0,0,1,12,4.82,27.56,27.56,0,0,1,4.76,7.11Z"/></g>',
  },
  'pi pi-desktop': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="desktop"><path d="M19,16.75H5A1.76,1.76,0,0,1,3.25,15V5A1.76,1.76,0,0,1,5,3.25H19A1.76,1.76,0,0,1,20.75,5V15A1.76,1.76,0,0,1,19,16.75ZM5,4.75A.25.25,0,0,0,4.75,5V15a.25.25,0,0,0,.25.25H19a.25.25,0,0,0,.25-.25V5A.25.25,0,0,0,19,4.75Z"/><path d="M15,20.75H12a.76.76,0,0,1-.75-.75V16a.75.75,0,0,1,1.5,0v3.25H15a.75.75,0,0,1,0,1.5Z"/><path d="M12,20.75H9a.75.75,0,0,1,0-1.5h3a.75.75,0,0,1,0,1.5Z"/></g>',
  },
  'pi pi-images': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="images"><path d="M18.5,3.75H8.5A2.75,2.75,0,0,0,5.75,6.5v.25H5.5A2.75,2.75,0,0,0,2.75,9.5v8A2.75,2.75,0,0,0,5.5,20.25h10a2.75,2.75,0,0,0,2.75-2.75v-.25h.25a2.75,2.75,0,0,0,2.75-2.75v-8A2.75,2.75,0,0,0,18.5,3.75ZM7.25,6.5A1.25,1.25,0,0,1,8.5,5.25h10A1.25,1.25,0,0,1,19.75,6.5v6.2l-2.27-1.91a.74.74,0,0,0-1.05.08l-1.07,1.26-4-3.88A.7.7,0,0,0,10.84,8a.75.75,0,0,0-.54.26L7.25,11.89ZM8.5,15.75A1.25,1.25,0,0,1,7.25,14.5v-.3l3.67-4.32,3.46,3.39-2.1,2.48Zm8.25,1.75a1.25,1.25,0,0,1-1.25,1.25H5.5A1.25,1.25,0,0,1,4.25,17.5v-8A1.25,1.25,0,0,1,5.5,8.25h.25V14.5A2.75,2.75,0,0,0,8.5,17.25h8.25Zm1.75-1.75H14.25l2.84-3.34,2.63,2.23A1.23,1.23,0,0,1,18.5,15.75Z"/></g>',
  },
  'pi pi-users': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="users"><path d="M14,12.25A3.75,3.75,0,1,1,17.75,8.5,3.75,3.75,0,0,1,14,12.25Zm0-6A2.25,2.25,0,1,0,16.25,8.5,2.25,2.25,0,0,0,14,6.25Z"/><path d="M21,19.25a.76.76,0,0,1-.75-.75c0-1.95-1.06-3.25-6.25-3.25s-6.25,1.3-6.25,3.25a.75.75,0,0,1-1.5,0c0-4.75,5.43-4.75,7.75-4.75s7.75,0,7.75,4.75A.76.76,0,0,1,21,19.25Z"/><path d="M8.32,13.06H8a3,3,0,1,1,.58-6,.75.75,0,1,1-.15,1.49,1.46,1.46,0,0,0-1.09.34,1.47,1.47,0,0,0-.54,1,1.49,1.49,0,0,0,1.35,1.64,1.53,1.53,0,0,0,.93-.22.75.75,0,0,1,.79,1.28A3,3,0,0,1,8.32,13.06Z"/><path d="M3,18.5a.76.76,0,0,1-.75-.75c0-2.7.72-4.5,4.25-4.5a.75.75,0,0,1,0,1.5c-2.35,0-2.75.75-2.75,3A.76.76,0,0,1,3,18.5Z"/></g>',
  },
  'pi pi-book': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="book"><path d="M19,3.25H6.75A2.43,2.43,0,0,0,4.25,5.6V18a2.85,2.85,0,0,0,2.94,2.75H19a.76.76,0,0,0,.75-.75V4A.76.76,0,0,0,19,3.25Zm-.75,16H7.19A1.35,1.35,0,0,1,5.75,18a1.35,1.35,0,0,1,1.44-1.25H18.25Zm0-4H7.19a3,3,0,0,0-1.44.37V5.6a.94.94,0,0,1,1-.85h11.5Z"/><path d="M8.75,8.75h6.5a.75.75,0,0,0,0-1.5H8.75a.75.75,0,0,0,0,1.5Z"/><path d="M8.75,12.25h6.5a.75.75,0,0,0,0-1.5H8.75a.75.75,0,0,0,0,1.5Z"/></g>',
  },
  'pi pi-palette': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="palette"><path d="M12,3.25A8.75,8.75,0,0,0,3.25,12a8.59,8.59,0,0,0,8.58,8.75h.05a2.87,2.87,0,0,0,2.87-2.88,2.66,2.66,0,0,0-.27-1.2,2.07,2.07,0,0,1-.19-.82,1.15,1.15,0,0,1,1.15-1.15H17A3.75,3.75,0,0,0,20.75,11,7.76,7.76,0,0,0,12,3.25Zm5,9.95H15.44a2.65,2.65,0,0,0-2.65,2.65,3.41,3.41,0,0,0,.33,1.43,1.16,1.16,0,0,1,.13.59,1.37,1.37,0,0,1-1.37,1.38A7.09,7.09,0,0,1,4.75,12,7.25,7.25,0,0,1,17,6.84,6.16,6.16,0,0,1,19.25,11,2.25,2.25,0,0,1,17,13.2Z"/><path d="M7.75,11a1.25,1.25,0,1,1,1.25-1.25A1.25,1.25,0,0,1,7.75,11Z"/><path d="M10.75,8.25A1.25,1.25,0,1,1,12,7,1.25,1.25,0,0,1,10.75,8.25Z"/><path d="M14.75,8.75A1.25,1.25,0,1,1,16,7.5,1.25,1.25,0,0,1,14.75,8.75Z"/><path d="M8.75,15.75A1.25,1.25,0,1,1,10,14.5,1.25,1.25,0,0,1,8.75,15.75Z"/></g>',
  },
  'pi pi-sign-out': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="sign-out"><path d="M9,20.75H6a2.64,2.64,0,0,1-2.75-2.53V5.78A2.64,2.64,0,0,1,6,3.25H9a.75.75,0,0,1,0,1.5H6a1.16,1.16,0,0,0-1.25,1V18.22a1.16,1.16,0,0,0,1.25,1H9a.75.75,0,0,1,0,1.5Z"/><path d="M16,16.75a.74.74,0,0,1-.53-.22.75.75,0,0,1,0-1.06L18.94,12,15.47,8.53a.75.75,0,1,1,1.06-1.06l4,4a.75.75,0,0,1,0,1.06l-4,4A.74.74,0,0,1,16,16.75Z"/><path d="M20,12.75H9a.75.75,0,0,1,0-1.5H20a.75.75,0,0,1,0,1.5Z"/></g>',
  },
  'pi pi-chevron-left': {
    viewBox: '0 0 24 24',
    markup:
      '<path d="M14,17.75a.74.74,0,0,1-.53-.22l-5-5a.75.75,0,0,1,0-1.06l5-5a.75.75,0,0,1,1.06,1.06L10.06,12l4.47,4.47a.75.75,0,0,1,0,1.06A.74.74,0,0,1,14,17.75Z"/>',
  },
  'pi pi-chevron-right': {
    viewBox: '0 0 24 24',
    markup:
      '<g id="chevron-right"><path d="M10,17.75a.74.74,0,0,1-.53-.22.75.75,0,0,1,0-1.06L13.94,12,9.47,7.53a.75.75,0,0,1,1.06-1.06l5,5a.75.75,0,0,1,0,1.06l-5,5A.74.74,0,0,1,10,17.75Z"/></g>',
  },
  'pi pi-folder': DEFAULT_ICON,
};

@Component({
  selector: 'app-sidebar-icon',
  standalone: true,
  templateUrl: './sidebar-icon.component.html',
  styleUrl: './sidebar-icon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarIconComponent {
  private sanitizer = inject(DomSanitizer);

  readonly icon = input.required<string>();
  readonly decorative = input(true);
  readonly ariaLabel = input<string | null>(null);

  readonly isAssetIcon = computed(() => /\.svg(?:[?#].*)?$/i.test(this.icon()));
  readonly assetIconSource = computed(() =>
    this.isAssetIcon() ? `url("${this.icon()}")` : null
  );

  readonly iconDefinition = computed<SidebarIconDefinition>(() => {
    return SIDEBAR_ICON_REGISTRY[this.icon()] ?? DEFAULT_ICON;
  });

  readonly iconMarkup = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.iconDefinition().markup)
  );
}
